/**
 * app-posture-brief.ts — Editorial copy layer for the App Posture page
 * standfirst (the page formerly known as App Permissions).
 *
 * The "score" here is inventory composition, not a 0-100 number — the
 * headline and verdict key off the tier-one (Critical + High) app count,
 * the unverified-tier-one count, and recent consent volume, in that
 * precedence order.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table, no seed, no rotation — the same input always
 * produces the same brief, and all variation in what the user reads comes
 * from the data, never from picking among synonymous phrasings of the
 * same fact.
 *
 * Signal precedence (headline and verdict route off the same signals so
 * the verdict never contradicts the headline):
 *   1. Volatile   — newThisWeek is a known count >= 5.
 *   2. Unverified — unverifiedTier1Count is a known count >= 1.
 *   3. Sprawl     — tier1 > 5.
 *   4. Few        — 1 <= tier1 <= 5.
 *   5. All-clear  — tier1 === 0 (a known zero from the summary).
 *
 * Loading state: when `summary` is null/undefined, or when its
 * criticalCount/highCount aren't known numbers, we return a neutral
 * "Reading inventory…" brief rather than guessing.
 *
 * Action-first fields (blueprint §6.2 row 7): `headline` follows the same
 * signal precedence; `lede` names the tier-one share of the inventory and,
 * when known, the consents of the last 7 days. `newThisWeek` is a rolling 7
 * days, not a calendar week, so the new copy says "in the last 7 days".
 */

import type {
  OAuthApp,
  OAuthAppSummary,
} from '@/components/SecurityCenter/appPermissionsTypes'
import { briefProvenance } from '@/lib/brief-helpers'
import type { ActionBriefFields } from '@/lib/brief-shape'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface AppPostureBrief {
  verdict: string
  /** The provenance line (rule 4, D36): "Synced by Guardian at 06:12 today", or "" while the time is unknown. Kept under its old name for PillarBrief. */
  byline: string
  delta: { value: number; days: number } | null
  /** Action-first h1 (blueprint §6.2 row 7). */
  headline: string
  /** Never set by this composer: its headlines name counts, not a subject. */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
}

export interface AppPostureBriefInput {
  /** Signed-in user email. Unused since the byline became the provenance line
   *  (rule 5 bans bylines); kept for call-site compatibility. */
  userEmail?: string
  /** The provenance clock's "today"; tests only. */
  now?: Date
  /** Active tenant ID. Callers still pass this; unused by the composer now
   *  that copy is deterministic rather than seeded per tenant per day. */
  tenantId?: string
  summary?: OAuthAppSummary | null
  /** Apps consented in the last 7 days. `null`/`undefined` means unknown. */
  newThisWeek?: number | null
  /** 30-day delta in tier-1 count (Critical + High). `null`/`undefined`
   *  means unknown — never treated as a known zero. */
  tier1Delta30d?: number | null
  /** Count of tier-one apps from unverified, non-first-party publishers.
   *  `undefined` means unknown (e.g. the underlying app list hasn't
   *  loaded yet) — never treated as a known zero. */
  unverifiedTier1Count?: number
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** A finite number, or `undefined` when the caller's value isn't one. An
 *  unknown count/delta must never be coerced to zero. */
function isFiniteNumber(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x)
}

/** Capitalized word form for 1-9, digits from 10 up — used sentence-initial
 *  (verdict) so the count reads as prose rather than a bare digit. */
function countWord1to9(n: number): string {
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  return n >= 1 && n <= 9 ? words[n] : String(n)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  VOLATILE: '{newWeek} new apps this week.',
  UNVERIFIED1: 'One unverified publisher holds tier-one scopes.',
  UNVERIFIED: '{N} unverified publishers hold tier-one scopes.',
  SPRAWL: 'Prune tier-one scopes this sprint.',
  FEW_ONE: 'One tier-one app to review.',
  FEW: '{N} tier-one apps to review.',
  ROUTINE_WATCH: 'Routine watch only.',
}

/** Action-first h1 (blueprint §6.2 row 7), same signal precedence as the verdict. */
const HEADLINE_ACTION = {
  LOADING: 'Reading your app inventory.',
  VOLATILE: 'Review the {n} apps consented in the last 7 days first.',
  UNVERIFIED_ONE: 'Start with the unverified publisher.',
  UNVERIFIED: 'Start with the {n} unverified publishers.',
  TIER1_ONE: 'Review the tier-one app this week.',
  TIER1: 'Review the {N} tier-one apps this week.',
  CLEAR: 'Nothing to review.',
}

/** The lede: the tier-one share of the inventory, then the rolling 7-day consents. */
const LEDE = {
  LOADING: '**Guardian is reading your app inventory**.',
  SHARE_NONE: '**None of the {N} apps hold tier-one permissions**',
  SHARE_ONE: '**1 of {N} apps holds tier-one permissions**',
  SHARE: '**{t} of {N} apps hold tier-one permissions**',
  COUNT_NONE: '**No app holds tier-one permissions**',
  COUNT_ONE: '**1 app holds tier-one permissions**',
  COUNT: '**{t} apps hold tier-one permissions**',
  NEW_NONE: ', and none were consented in the last 7 days',
  NEW_ONE: ', and 1 was consented in the last 7 days',
  NEW: ', and {w} were consented in the last 7 days',
}

/* -------------------------------------------------------------------------- */
/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

function pickVerdict(
  tier1: number,
  unverified: number | undefined,
  newWeek: number | undefined,
  volatile: boolean,
): string {
  if (volatile) return VERDICT.VOLATILE.replace('{newWeek}', String(newWeek))
  if (unverified !== undefined && unverified >= 1) {
    if (unverified === 1) return VERDICT.UNVERIFIED1
    return VERDICT.UNVERIFIED.replace('{N}', countWord1to9(unverified))
  }
  if (tier1 > 5) return VERDICT.SPRAWL
  if (tier1 >= 1) {
    if (tier1 === 1) return VERDICT.FEW_ONE
    return VERDICT.FEW.replace('{N}', countWord1to9(tier1))
  }
  return VERDICT.ROUTINE_WATCH
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 7)                        */
/* -------------------------------------------------------------------------- */

function pickActionHeadline(
  tier1: number,
  unverified: number | undefined,
  newWeek: number | undefined,
  volatile: boolean,
): string {
  if (volatile) return HEADLINE_ACTION.VOLATILE.replace('{n}', String(newWeek))
  if (unverified !== undefined && unverified >= 1) {
    return unverified === 1
      ? HEADLINE_ACTION.UNVERIFIED_ONE
      : HEADLINE_ACTION.UNVERIFIED.replace('{n}', String(unverified))
  }
  if (tier1 === 1) return HEADLINE_ACTION.TIER1_ONE
  if (tier1 >= 2) return HEADLINE_ACTION.TIER1.replace('{N}', String(tier1))
  return HEADLINE_ACTION.CLEAR
}

function buildLede(tier1: number, total: number | undefined, newWeek: number | undefined): string {
  let head: string
  // "{t} of {N}" only when the total can hold the tier-one count.
  if (typeof total === 'number' && total > 0 && total >= tier1) {
    const tpl = tier1 === 0 ? LEDE.SHARE_NONE : tier1 === 1 ? LEDE.SHARE_ONE : LEDE.SHARE
    head = tpl.replace('{t}', String(tier1)).replace('{N}', String(total))
  } else {
    const tpl = tier1 === 0 ? LEDE.COUNT_NONE : tier1 === 1 ? LEDE.COUNT_ONE : LEDE.COUNT
    head = tpl.replace('{t}', String(tier1))
  }
  if (newWeek === undefined) return `${head}.`
  const clause =
    newWeek === 0 ? LEDE.NEW_NONE : newWeek === 1 ? LEDE.NEW_ONE : LEDE.NEW.replace('{w}', String(newWeek))
  return `${head}${clause}.`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose an AppPostureBrief from the active OAuth app inventory state. All
 * inputs are optional — the function degrades gracefully toward a plausible
 * "no data" brief rather than throwing.
 */
export function composeAppPostureBrief(
  input: AppPostureBriefInput = {},
): AppPostureBrief {
  const summary = input.summary
  // The provenance line: the inventory's last sync (`lastSyncAt`), "Synced"
  // because the inventory is Microsoft's as is (terminology decision 5). No
  // line while the summary loads.
  const apps = summary && isFiniteNumber(summary.totalApps) ? summary.totalApps : undefined
  const byline = summary
    ? briefProvenance(summary.lastSyncAt ?? null, {
        verb: 'Synced',
        scope: apps === undefined ? undefined : `${apps} ${apps === 1 ? 'app' : 'apps'}`,
        now: input.now,
      })
    : ''
  const tier1Known =
    summary != null && isFiniteNumber(summary.criticalCount) && isFiniteNumber(summary.highCount)

  if (!tier1Known) {
    return {
      verdict: 'Reading inventory…',
      byline,
      delta: null,
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const s = summary as OAuthAppSummary
  const tier1 = s.criticalCount + s.highCount
  const total = apps
  const unverified = isFiniteNumber(input.unverifiedTier1Count) ? input.unverifiedTier1Count : undefined
  const newWeek = isFiniteNumber(input.newThisWeek) ? input.newThisWeek : undefined
  const volatile = newWeek !== undefined && newWeek >= 5
  const deltaKnown = isFiniteNumber(input.tier1Delta30d)
  const deltaValue = deltaKnown ? (input.tier1Delta30d as number) : 0

  const verdict = pickVerdict(tier1, unverified, newWeek, volatile)

  return {
    verdict,
    byline,
    delta: deltaKnown ? { value: deltaValue, days: 30 } : null,
    headline: pickActionHeadline(tier1, unverified, newWeek, volatile),
    lede: buildLede(tier1, total, newWeek),
  }
}

/* -------------------------------------------------------------------------- */
/* Why-it-matters fallback                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Compose the "why-it-matters" paragraph for an app card on the App Posture
 * triptych (§8.2). Returns the AI-generated `aiRiskSummary` verbatim when
 * present so the operator gets the model's reasoning. When the AI hasn't run
 * yet, falls back to a deterministic template stitched from the real per-app
 * counts — `criticalPermissionCount`, `highPermissionCount`,
 * `daysSinceLastSignIn`. Every clause is conditional on a real field being
 * present; we never fabricate prose for an empty app.
 *
 * Markdown emphasis (`**...**`) is preserved so the consumer can render it
 * via the same `<strong>`-substitution pattern PillarBrief uses.
 */
export function whyItMatters(
  app: Pick<
    OAuthApp,
    | 'aiRiskSummary'
    | 'criticalPermissionCount'
    | 'highPermissionCount'
    | 'daysSinceLastSignIn'
  >,
): string {
  if (app.aiRiskSummary && app.aiRiskSummary.trim().length > 0) {
    return app.aiRiskSummary
  }

  const parts: string[] = []

  if (app.criticalPermissionCount > 0) {
    const plural = app.criticalPermissionCount !== 1 ? 's' : ''
    parts.push(
      `Holds **${app.criticalPermissionCount} critical** permission${plural}`,
    )
  }

  if (app.highPermissionCount > 0) {
    const plural = app.highPermissionCount !== 1 ? 's' : ''
    parts.push(
      `${app.highPermissionCount} high-risk permission${plural}`,
    )
  }

  const days = app.daysSinceLastSignIn
  if (days != null && days > 30) {
    parts.push(`last used ${days} days ago, possibly stale`)
  } else if (days == null) {
    parts.push('no sign-in record')
  }

  if (parts.length === 0) {
    // No critical / high counts and a recent sign-in — there's not much to
    // say without inventing a verdict. Surface a neutral, honest sentence.
    return 'No critical or high-risk scopes flagged. Review for least-privilege fit.'
  }

  return parts.join('. ') + '.'
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  LEDE,
}
